import React, { useEffect, useState, useMemo } from 'react';
import {
    Modal,
    Table,
    Switch,
    InputNumber,
    Button,
    Space,
    Typography,
    Alert,
    Tag,
    Card,
    Row,
    Col,
    Divider,
    message,
    Tooltip
} from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    SettingOutlined,
    ThunderboltOutlined,
    MinusCircleOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import { RubricItem, DesignTestsConfig, createDefaultDesignTestsConfig } from '../../disseny/model/DesignTestConfig';
import {
    extractErRubricItems,
    extractScriptRubricItems,
    extractRelationalRubricItems,
    distributeEqually100
} from '../../disseny/engine/RubricExtractor';
import { parseScriptTextToData } from '../../disseny/engine/ScriptRelationalEngine';

const { Title, Text, Paragraph } = Typography;

interface ConfiguracioTestsDissenyModalProps {
    visible: boolean;
    onClose: (savedConfig?: DesignTestsConfig) => void;
    tipus: string; // 'ER', 'Script', 'Relacional'
    solutionRaw: string; // El JSON o text de solució
    initialChecksStr?: string; // El contingut actual del camp checks
    exerciseName?: string;
}

export const ConfiguracioTestsDissenyModal: React.FC<ConfiguracioTestsDissenyModalProps> = ({
    visible,
    onClose,
    tipus,
    solutionRaw,
    initialChecksStr,
    exerciseName = 'Exercici'
}) => {
    const [config, setConfig] = useState<DesignTestsConfig>(() => createDefaultDesignTestsConfig());
    const [penalitzacio, setPenalitzacio] = useState<number>(0);

    // Carregar la configuració existent a partir del string de checks o extreure-la de la solució
    useEffect(() => {
        if (!visible) return;

        let parsedConfig: DesignTestsConfig | null = null;
        let penalitatInicial = 0;

        if (initialChecksStr && initialChecksStr.includes('{') && initialChecksStr.includes('"items"')) {
            try {
                // Si el camp checks té el prefix tipus ER-VALIDATION#...
                let jsonPart = initialChecksStr;
                const hashIndex = initialChecksStr.indexOf('#');
                if (hashIndex !== -1 && initialChecksStr.slice(hashIndex + 1).trim().startsWith('{')) {
                    jsonPart = initialChecksStr.slice(hashIndex + 1).trim();
                }
                const parsed = JSON.parse(jsonPart);
                if (parsed && Array.isArray(parsed.items)) {
                    parsedConfig = parsed;
                    penalitatInicial = Number(parsed.penalitzacioPerIntent) || 0;
                }
            } catch (err) {
                console.error("No s'ha pogut parsejar la configuració de tests:", err);
            }
        }

        setPenalitzacio(penalitatInicial);

        // Extreure els ítems de la solució oficial actual
        let extractedItems: RubricItem[] = [];

        if (solutionRaw && solutionRaw.trim()) {
            try {
                if (tipus === 'ER') {
                    const solObj = typeof solutionRaw === 'string' ? JSON.parse(solutionRaw) : solutionRaw;
                    extractedItems = extractErRubricItems(solObj, parsedConfig);
                } else if (tipus === 'Script') {
                    let solObj;
                    if (solutionRaw.trim().startsWith('{')) {
                        solObj = JSON.parse(solutionRaw);
                    } else {
                        solObj = parseScriptTextToData(solutionRaw);
                    }
                    extractedItems = extractScriptRubricItems(solObj, parsedConfig);
                } else if (tipus === 'Relacional') {
                    const solObj = typeof solutionRaw === 'string' ? JSON.parse(solutionRaw) : solutionRaw;
                    extractedItems = extractRelationalRubricItems(solObj, parsedConfig);
                }
            } catch (ex) {
                console.warn("La solució no és un format vàlid per extreure ítems:", ex);
            }
        }

        setConfig({
            version: 1,
            penalitzacioPerIntent: penalitatInicial,
            items: extractedItems
        });
    }, [visible, tipus, solutionRaw, initialChecksStr]);

    // Suma dels punts dels ítems actius
    const totalPunts = useMemo(() => {
        return (config.items || []).filter((i: RubricItem) => i.enabled).reduce((sum: number, i: RubricItem) => sum + (Number(i.points) || 0), 0);
    }, [config.items]);

    const activeCount = useMemo(() => {
        return (config.items || []).filter((i: RubricItem) => i.enabled).length;
    }, [config.items]);

    const isValid100 = totalPunts === 100;

    // Toggle avaluar / no avaluar ítem
    const handleToggleItem = (id: string, checked: boolean) => {
        setConfig((prev: DesignTestsConfig) => ({
            ...prev,
            items: prev.items.map((it: RubricItem) => it.id === id ? { ...it, enabled: checked, points: checked ? it.points : 0 } : it)
        }));
    };

    // Canvi de puntuació d'un ítem
    const handleChangePoints = (id: string, val: number | null) => {
        const num = val !== null && val >= 0 ? val : 0;
        setConfig((prev: DesignTestsConfig) => ({
            ...prev,
            items: prev.items.map((it: RubricItem) => it.id === id ? { ...it, points: num } : it)
        }));
    };

    // Repartir equitativament 100 punts
    const handleDistributeEqually = () => {
        setConfig((prev: DesignTestsConfig) => ({
            ...prev,
            items: distributeEqually100(prev.items)
        }));
        message.success("S'han repartit exactament 100 punts entre els elements seleccionats!");
    };

    // Marcar tots com a avaluables o cap
    const handleToggleAll = (enabled: boolean) => {
        setConfig((prev: DesignTestsConfig) => {
            const newItems = prev.items.map((it: RubricItem) => ({ ...it, enabled }));
            return {
                ...prev,
                items: enabled ? distributeEqually100(newItems) : newItems.map((it: RubricItem) => ({ ...it, points: 0 }))
            };
        });
    };

    // Guardar configuració
    const handleSave = () => {
        if (config.items.length > 0 && activeCount === 0) {
            message.error("Has de seleccionar com a mínim un element per avaluar.");
            return;
        }

        if (config.items.length > 0 && !isValid100) {
            message.error(`La suma total dels punts ha de ser exactament de 100 (actualment és ${totalPunts}).`);
            return;
        }

        const finalConfig: DesignTestsConfig = {
            version: 1,
            penalitzacioPerIntent: Number(penalitzacio) || 0,
            items: config.items
        };

        message.success("Configuració de tests i rúbrica de disseny desada correctament!");
        onClose(finalConfig);
    };

    const columns = [
        {
            title: 'Avaluar?',
            dataIndex: 'enabled',
            key: 'enabled',
            width: 90,
            align: 'center' as const,
            render: (enabled: boolean, record: RubricItem) => (
                <Switch
                    checked={enabled}
                    onChange={(checked) => handleToggleItem(record.id, checked)}
                    size="small"
                />
            )
        },
        {
            title: 'Categoria',
            dataIndex: 'category',
            key: 'category',
            width: 120,
            render: (cat: string) => {
                let color = 'blue';
                if (cat === 'entity' || cat === 'table') color = 'geekblue';
                else if (cat === 'relationship') color = 'purple';
                else if (cat === 'pk') color = 'gold';
                else if (cat === 'fk') color = 'cyan';
                else if (cat === 'hierarchy') color = 'magenta';
                return <Tag color={color}>{cat.toUpperCase()}</Tag>;
            }
        },
        {
            title: 'Element Avaluable',
            dataIndex: 'label',
            key: 'label',
            render: (label: string, record: RubricItem) => (
                <div>
                    <Text strong={record.enabled} style={{ color: record.enabled ? '#000' : '#888' }}>
                        {label}
                    </Text>
                    {record.details && (
                        <div style={{ fontSize: 12, color: '#888' }}>
                            {record.details}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Puntuació (Punts)',
            dataIndex: 'points',
            key: 'points',
            width: 140,
            align: 'center' as const,
            render: (points: number, record: RubricItem) => (
                <Space>
                    <InputNumber
                        min={0}
                        max={100}
                        value={record.points}
                        disabled={!record.enabled}
                        onChange={(val) => handleChangePoints(record.id, val)}
                        style={{ width: 70 }}
                        size="small"
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>pts</Text>
                </Space>
            )
        }
    ];

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <SettingOutlined style={{ color: '#1890ff', fontSize: 20 }} />
                    <span style={{ fontWeight: 700, fontSize: 16 }}>
                        Configuració de Tests i Rúbrica d'Avaluació · {tipus}
                    </span>
                </div>
            }
            open={visible}
            onCancel={() => onClose()}
            width={850}
            style={{ top: 20 }}
            footer={[
                <Button key="cancel" onClick={() => onClose()}>
                    Cancel·lar
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    onClick={handleSave}
                    disabled={config.items.length > 0 && !isValid100}
                    style={{
                        backgroundColor: (config.items.length === 0 || isValid100) ? '#52c41a' : undefined,
                        borderColor: (config.items.length === 0 || isValid100) ? '#52c41a' : undefined
                    }}
                >
                    Aplicar Configuració (100 Punts)
                </Button>
            ]}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
                    Configureu quins elements de la solució oficial es verificaran automàticament en els enviaments de l'alumnat.
                    Cada criteri encertat suma els seus punts. La puntuació total dels criteris actius <strong>ha de sumar exactament 100 punts</strong> (escala 0-10 real).
                </Paragraph>

                {/* Caixa de configuració de penalització per intents fallits */}
                <Card size="small" style={{ background: '#fffbe6', borderColor: '#ffe58f' }}>
                    <Row align="middle" justify="space-between">
                        <Col>
                            <Space align="center">
                                <MinusCircleOutlined style={{ color: '#fa8c16', fontSize: 18 }} />
                                <div>
                                    <Text strong>Penalització per cada intent fallit:</Text>
                                    <div style={{ fontSize: 12, color: '#777' }}>
                                        Resta punts de la nota final per cada intent erroni enviat per l'alumne abans d'aconseguir la solució.
                                    </div>
                                </div>
                            </Space>
                        </Col>
                        <Col>
                            <Space align="center">
                                <InputNumber
                                    min={0}
                                    max={50}
                                    step={1}
                                    value={penalitzacio}
                                    onChange={(v) => setPenalitzacio(v !== null ? v : 0)}
                                    style={{ width: 80 }}
                                />
                                <Text strong>punts / intent</Text>
                            </Space>
                        </Col>
                    </Row>
                </Card>

                {/* Comprovació de solució vàlida */}
                {config.items.length === 0 ? (
                    <Alert
                        message="Sense elements avaluables detectats a la solució"
                        description="Per poder configurar els tests d'aquest exercici, primer heu de definir i desar la solució oficial (ER, Script o Relacional) a l'editor de dalt."
                        type="warning"
                        showIcon
                    />
                ) : (
                    <>
                        {/* Barra d'estat de puntuació */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 16px',
                            background: isValid100 ? '#f6ffed' : '#fff1f0',
                            border: `1px solid ${isValid100 ? '#b7eb8f' : '#ffa39e'}`,
                            borderRadius: 6
                        }}>
                            <Space size="middle">
                                <div>
                                    <Text strong>Elements avaluats:</Text>{' '}
                                    <Tag color="blue">{activeCount} de {config.items.length}</Tag>
                                </div>
                                <div>
                                    <Text strong>Puntuació Total:</Text>{' '}
                                    <Tag
                                        color={isValid100 ? 'success' : 'error'}
                                        style={{ fontSize: 14, fontWeight: 700, padding: '2px 10px' }}
                                    >
                                        {totalPunts} / 100
                                    </Tag>
                                </div>
                            </Space>

                            <Space>
                                <Button
                                    size="small"
                                    icon={<ThunderboltOutlined />}
                                    onClick={handleDistributeEqually}
                                    type="primary"
                                    ghost
                                >
                                    Repartir 100 pts equitativament
                                </Button>
                                <Button size="small" onClick={() => handleToggleAll(true)}>
                                    Tots
                                </Button>
                                <Button size="small" onClick={() => handleToggleAll(false)}>
                                    Cap
                                </Button>
                            </Space>
                        </div>

                        {!isValid100 && (
                            <Alert
                                message={`Falten / Sobren punts: La suma actual és de ${totalPunts}. Ha de ser exactament de 100 punts.`}
                                type="error"
                                showIcon
                                style={{ padding: '6px 12px' }}
                            />
                        )}

                        {/* Taula de criteris */}
                        <Table
                            size="small"
                            rowKey="id"
                            columns={columns}
                            dataSource={config.items}
                            pagination={false}
                            scroll={{ y: 320 }}
                            style={{ border: '1px solid #f0f0f0', borderRadius: 6 }}
                        />
                    </>
                )}
            </div>
        </Modal>
    );
};

export default ConfiguracioTestsDissenyModal;
